/*
 * ตาราง → วางจาก Excel: what Excel / Google Sheets / Numbers put on the clipboard, as cells for pasteGrid
 * (src/table/tableModel.ts). The app only (it reads HTML with the browser's DOM); the server never sees a clipboard.
 */
import { TABLE_MAX_COLS, TABLE_MAX_ROWS, type PasteGrid, type TableCell, type TableMerge } from './tableModel';

const styleOf = (c: TableCell): TableCell => { const { t: _t, tr: _tr, ...s } = c; return { t: '', ...s }; };

/**
 * Excel's / Google Sheets' plain text: a tab between cells, a line per row; a cell with a line break, tab or quote is
 * in quotes ("" for a quote). The last line break is the end of the last row, not an empty row.
 */
export function parseTsv(text: string): string[][] {
  const s = text.replace(/\r\n?/g, '\n');
  const rows: string[][] = [];
  let row: string[] = [], cell = '', i = 0, quoted = false, startOfCell = true;
  while (i < s.length) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"' && s[i + 1] === '"') { cell += '"'; i += 2; continue; }
      if (ch === '"') { quoted = false; i++; continue; }
      cell += ch; i++; continue;
    }
    if (ch === '"' && startOfCell) { quoted = true; startOfCell = false; i++; continue; }
    if (ch === '\t') { row.push(cell); cell = ''; startOfCell = true; i++; continue; }
    if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; startOfCell = true; i++; continue; }
    cell += ch; startOfCell = false; i++;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  const w = Math.max(0, ...rows.map(r => r.length));
  return rows.map(r => [...r, ...Array(w - r.length).fill('')]);
}

/** text that came from a sheet (cells, not a sentence typed somewhere else) */
export const looksLikeCells = (text: string) => text.includes('\t') || (/\n./.test(text.replace(/\r\n?/g, '\n').replace(/\n+$/, '')) && /\r?\n$/.test(text));

const NAMED: Record<string, string> = { black: '#000000', white: '#ffffff', red: '#ff0000', green: '#008000', blue: '#0000ff', yellow: '#ffff00', gray: '#808080', grey: '#808080' };
/** "#abc", "#aabbcc", "rgb(1, 2, 3)", a few names → "#aabbcc"; anything else (windowtext, transparent, auto) → null */
export function hexOf(v: string): string | null {
  const s = v.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(s)) return s;
  if (/^#[0-9a-f]{3}$/.test(s)) return '#' + [...s.slice(1)].map(x => x + x).join('');
  const m = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,/]+([\d.]+%?))?\s*\)$/.exec(s);
  if (m) {
    if (m[4] !== undefined && parseFloat(m[4]) === 0) return null;
    return '#' + [m[1], m[2], m[3]].map(x => Math.min(255, Number(x)).toString(16).padStart(2, '0')).join('');
  }
  return NAMED[s] ?? null;
}

/** "a:b; c:d" → { a: 'b', c: 'd' } (Excel writes `mso-` rules too; they are kept but not read) */
function declarations(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of text.split(';')) {
    const i = part.indexOf(':');
    if (i > 0) out[part.slice(0, i).trim().toLowerCase()] = part.slice(i + 1).trim().replace(/\s*!important$/, '');
  }
  return out;
}

/**
 * The table Excel / Google Sheets / Numbers put on the clipboard as HTML: its text, merged cells (colspan/rowspan)
 * and the looks a cell can have here (bold, alignment, fill, ink). Excel writes looks as classes in a <style>
 * (.xl65 {...}); Google Sheets inline. `doc` is the parsed HTML (DOMParser in the app). null = no table in it.
 */
export function parseClipHtml(doc: Document): PasteGrid | null {
  const table = doc.querySelector('table');
  if (!table) return null;
  const classes = new Map<string, Record<string, string>>();
  for (const st of [...doc.querySelectorAll('style')]) {
    const css = (st.textContent ?? '').replace(/\/\*[\s\S]*?\*\//g, '').replace(/<!--|-->/g, '');
    for (const m of css.matchAll(/([^{}]+)\{([^}]*)\}/g)) {
      for (const sel of m[1].split(',')) {
        const k = /\.([\w-]+)\s*$/.exec(sel.trim());
        if (k) classes.set(k[1], { ...classes.get(k[1]), ...declarations(m[2]) });
      }
    }
  }
  const grid: Array<Array<TableCell | undefined>> = [];
  const merges: TableMerge[] = [];
  let looks = false;
  const rows = [...table.querySelectorAll('tr')].filter(tr => tr.closest('table') === table);
  rows.forEach((tr, r) => {
    grid[r] ??= [];
    let c = 0;
    for (const td of [...tr.children].filter(x => /^(TD|TH)$/i.test(x.tagName))) {
      while (grid[r][c] !== undefined) c++;
      const cs = Math.max(1, Math.min(TABLE_MAX_COLS, Number(td.getAttribute('colspan')) || 1));
      const rs = Math.max(1, Math.min(TABLE_MAX_ROWS, Number(td.getAttribute('rowspan')) || 1));
      // the text as seen: <br> is a new line, other runs of spaces are one space
      const clone = td.cloneNode(true) as Element;
      clone.querySelectorAll('br').forEach(br => br.replaceWith('\u0001'));
      const text = (clone.textContent ?? '').replace(/ /g, ' ').replace(/[ \t\r\n]+/g, ' ').split('\u0001').map(x => x.trim()).join('\n').trim();
      const css: Record<string, string> = {};
      for (const cls of (td.getAttribute('class') ?? '').split(/\s+/)) Object.assign(css, classes.get(cls));
      Object.assign(css, declarations(td.getAttribute('style') ?? ''));
      const cell: TableCell = { t: text };
      const weight = css['font-weight'] ?? '';
      if (weight === 'bold' || weight === 'bolder' || Number(weight) >= 600 || td.querySelector('b,strong') || td.tagName === 'TH') cell.b = true;
      const al = (css['text-align'] ?? td.getAttribute('align') ?? '').toLowerCase();
      if (al === 'center') cell.al = 'c'; else if (al === 'right') cell.al = 'r'; else if (al === 'left') cell.al = 'l';
      const va = (css['vertical-align'] ?? td.getAttribute('valign') ?? '').toLowerCase();
      if (va === 'top') cell.va = 't'; else if (va === 'middle') cell.va = 'm'; else if (va === 'bottom') cell.va = 'b';
      const bg = hexOf(css['background-color'] ?? css['background'] ?? td.getAttribute('bgcolor') ?? '');
      if (bg) cell.bg = bg;
      const ink = hexOf(css['color'] ?? '');
      if (ink && ink !== '#000000') cell.c = ink;
      if (cell.b || cell.al || cell.va || cell.bg || cell.c) looks = true;
      for (let y = 0; y < rs; y++) {
        grid[r + y] ??= [];
        for (let x = 0; x < cs; x++) grid[r + y][c + x] = y === 0 && x === 0 ? cell : { ...styleOf(cell) };
      }
      if (rs * cs > 1) merges.push({ r, c, rs, cs });
      c += cs;
    }
  });
  const w = Math.max(0, ...grid.map(row => row.length));
  if (!grid.length || !w) return null;
  const cells = grid.map(row => Array.from({ length: w }, (_, c) => row[c] ?? { t: '' }));
  // a merge cut by the end of the rows written (rowspan past the last <tr>) is kept to the rows there are
  return { cells, merges: merges.map(m => ({ ...m, rs: Math.min(m.rs, cells.length - m.r) })).filter(m => m.rs * m.cs > 1), looks };
}
